import "./index.scss";

import { Box, Button, ButtonGroup, Grid, Modal, Stack } from "@mui/material";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import { ReactComponent as UncheckedCircleIcon } from "assets/images/toggle-checkboxes/circle/off-normal.svg";
import { ReactComponent as CheckedCircleIcon } from "assets/images/toggle-checkboxes/circle/on-normal.svg";
import { ReactComponent as IconSeparator } from "assets/images/ui/dash-separator.svg";
import { default as CustomButton } from "components/Button";
import Input from "components/Input";
import { DATE_FORMAT, MYSQL_DATE_FORMAT } from "constants/index";
import { CardioWizardProtocol } from "models/workoutplan";
import moment from "moment";
import { memo, useCallback, useContext, useState } from "react";
import { toast } from "react-toastify";

import { WorkoutPlanContext } from "..";

const HeartRateZoneWizardModal = (props: {
  open: boolean;
  onClose: any;
  cardioWizardProtocols: CardioWizardProtocol[];
  memberDob: any;
}) => {
  const workoutPlanContext = useContext(WorkoutPlanContext);
  const { open, onClose, cardioWizardProtocols, memberDob } = props;
  const [selectedProtocol, setSelectedProtocol] = useState(cardioWizardProtocols[0]);
  const [defaultCalculationMethod, setDefaultCalculationMethod] = useState(
    cardioWizardProtocols[0].heart_rate_method
  );
  const [heartRateRangeMin, setHeartRateRangeMin] = useState(
    cardioWizardProtocols[0].perc_heart_rate_min
  );
  const [heartRateRangeMax, setHeartRateRangeMax] = useState(
    cardioWizardProtocols[0].perc_heart_rate_max
  );

  const getMembersAge = () => {
    //const dob = new Date(memberDob).getTime();
    const dob = moment(memberDob, MYSQL_DATE_FORMAT).format(DATE_FORMAT);
    const now = moment();
    const age = now.diff(dob, "years");
    return age;
  };

  //TODO: fetch the actual member age
  const [memberAge, setMemberAge] = useState(getMembersAge());
  const [restingHeartRate, setRestingHeartRate] = useState(70);

  //#region event handlers
  const handleCloseModal = useCallback(() => {
    onClose();
  }, []);

  const handleHeartRateZoneWizardSubmission = () => {
    let heartRateBpmMin = 0;
    let heartRateBpmMax = 0;

    // #region validation
    if (heartRateRangeMin === 0 || heartRateRangeMin > 100) {
      toast("Heart rate range min should be greater than 0 and less or equal to 100", {
        type: "error",
      });
      return;
    }
    if (heartRateRangeMax === 0 || heartRateRangeMax > 100) {
      toast("Heart rate range max should be greater than 0 and less or equal to 100", {
        type: "error",
      });
      return;
    }
    if (heartRateRangeMax <= heartRateRangeMin) {
      toast("Heart rate range max should be greater than heart rate range min", {
        type: "error",
      });
      return;
    }
    // #endregion validation
    if (defaultCalculationMethod === "STANDARD") {
      heartRateBpmMin = (220 - memberAge) * (heartRateRangeMin / 100);
      heartRateBpmMax = (220 - memberAge) * (heartRateRangeMax / 100);
    }
    if (defaultCalculationMethod === "KARVONEN") {
      heartRateBpmMin =
        (220 - memberAge - restingHeartRate) * (heartRateRangeMin / 100) + restingHeartRate;
      heartRateBpmMax =
        (220 - memberAge - restingHeartRate) * (heartRateRangeMax / 100) + restingHeartRate;
    }
    heartRateBpmMax = Math.floor(heartRateBpmMax);
    heartRateBpmMin = Math.floor(heartRateBpmMin);
    onClose({ heartRateBpmMax, heartRateBpmMin });
  };

  //#endregion event handlers

  return (
    <Modal
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
      }}
      open={open}
      onClose={handleCloseModal}
    >
      <Stack className="personal-details-modal-container heart-rate-zone-wizard">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          marginBottom="40px"
          className="header"
        >
          <h1>Heart Rate Zone Wizard</h1>
          <DeleteIcon width="24px" height="24px" onClick={handleCloseModal} />
        </Stack>
        <h2 className="headerSpacing">Protocol List</h2>
        <Box className={`searchable-list-container`}>
          <Box className="list">
            {cardioWizardProtocols.map((item, index) => (
              <button
                className={`checkable-button`}
                key={`hrwizardProtocol${index}`}
                onClick={() => {
                  setSelectedProtocol(item);
                  setDefaultCalculationMethod(
                    item.heart_rate_method === "CUSTOM" ? "STANDARD" : item.heart_rate_method
                  );
                  setHeartRateRangeMin(item.perc_heart_rate_min);
                  setHeartRateRangeMax(item.perc_heart_rate_max);
                }}
              >
                <Grid container>
                  <Grid item xs={11}>
                    <h2>{item.name}</h2>
                  </Grid>
                  <Grid item xs={1} className="check">
                    {selectedProtocol.id === item.id && <CheckedCircleIcon />}
                    {selectedProtocol.id !== item.id && <UncheckedCircleIcon />}
                  </Grid>
                  <Grid item xs={11} className="protocolDescription">
                    {item.description}
                  </Grid>
                  <Grid item xs={1}></Grid>
                </Grid>
              </button>
            ))}
          </Box>
        </Box>
        <Grid container alignItems={"center"} className="withMarginBottom" spacing={"7"}>
          <Grid item xs={3}>
            <span className="fieldLabel">Heart Rate Range</span>
          </Grid>
          <Grid item xs={3}>
            <Input
              disabled={selectedProtocol.name !== "Custom"}
              type="number"
              borderRightStraight={true}
              value={heartRateRangeMin}
              onChange={(v) => setHeartRateRangeMin(parseInt(v.target.value))}
            />
          </Grid>
          <Grid item xs={1} className="fieldLabel">
            Min
          </Grid>
          <Grid item xs={1} className="fieldLabel">
            <IconSeparator />
          </Grid>
          <Grid item xs={3}>
            <Input
              disabled={selectedProtocol.name !== "Custom"}
              type="number"
              borderLeftStraight={true}
              value={heartRateRangeMax}
              onChange={(v) => setHeartRateRangeMax(parseInt(v.target.value))}
            />
          </Grid>
          <Grid item xs={1} className="fieldLabel">
            Max
          </Grid>

          <Grid item xs={3}>
            <span className="fieldLabel">Method Options</span>
          </Grid>
          <Grid item xs={9}>
            <ButtonGroup className="button-group">
              <Button
                onClick={() => setDefaultCalculationMethod("STANDARD")}
                className={`${defaultCalculationMethod === "STANDARD" ? `active` : ``}`}
                disabled={selectedProtocol.heart_rate_method === "KARVONEN"}
              >
                Standard
              </Button>
              <Button
                onClick={() => setDefaultCalculationMethod("KARVONEN")}
                className={`${defaultCalculationMethod === "KARVONEN" ? `active` : ``}`}
                disabled={selectedProtocol.heart_rate_method === "STANDARD"}
              >
                Karvonen
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>
        {(!workoutPlanContext.isWorkoutPlanTemplate || defaultCalculationMethod === "KARVONEN") && (
          <h2 className="headerSpacing">Verify the following information</h2>
        )}
        <Grid container alignItems={"center"} className="withMarginBottom">
          {!workoutPlanContext.isWorkoutPlanTemplate && (
            <>
              <Grid item xs={2}>
                <span className="fieldLabel">Age</span>
              </Grid>
              <Grid item xs={10}>
                <Input disabled type="number" value={memberAge} />
              </Grid>
            </>
          )}
          {defaultCalculationMethod === "KARVONEN" && (
            <>
              <Grid item xs={2}>
                <span className="fieldLabel">Resting Heart Rate</span>
              </Grid>
              <Grid item xs={10}>
                <Input
                  type="number"
                  value={restingHeartRate}
                  onChange={(v) => setRestingHeartRate(parseInt(v.target.value))}
                />
              </Grid>
            </>
          )}
        </Grid>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" marginTop="30px">
          <Stack direction="row" alignItems="center" spacing={3}>
            <CustomButton label="Cancel" className="text-button" onClick={handleCloseModal} />
            <CustomButton
              label="OK"
              className="confirm-button"
              onClick={handleHeartRateZoneWizardSubmission}
            />
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default memo(HeartRateZoneWizardModal);
